import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useWorkingModel } from "@/hooks/useWorkingModel";
import { useDeliverable } from "@/hooks/useDeliverable";
import { PageHeader, RequireModel } from "@/components/PageParts";
import DeliverableState from "@/components/DeliverableState";
import PlanSVG from "@/components/PlanSVG";
import PlanActions from "@/components/PlanActions";
import TitleBlock from "@/components/TitleBlock";
import ZoomPan from "@/components/ZoomPan";
import { EngineeringDetailsButton } from "@/components/EngineeringDetails";
import { Button } from "@/components/ui";

const DISCIPLINES = [
  { key: "arch", ar: "معماري", en: "Architectural" },
  { key: "structural", ar: "إنشائي", en: "Structural" },
  { key: "electrical", ar: "كهرباء", en: "Electrical" },
  { key: "plumbing", ar: "تغذية مياه", en: "Water supply" },
  { key: "drainage", ar: "صرف صحي", en: "Sanitary drainage" },
  { key: "pool", ar: "مسابح", en: "Pools" },
];

function elecColor(type = "") {
  if (type.includes("light")) return "#FFD166";
  if (type.includes("db") || type.includes("panel")) return "#DC2626";
  if (type.includes("ac")) return "#2DD4BF";
  return "#F59E0B";
}

const asLine = (arr, color, dash, width) => (arr || [])
  .filter((p) => Array.isArray(p.polyline) && p.polyline.length >= 2)
  .map((p) => ({ pts: p.polyline, color, dash, width }));
const asPts = (arr, color, r) => (arr || []).map((p) => ({ x: p.x, y: p.y, color, r }));

/** MEP overlay (electrical / water / drainage) from the connected layout. */
function mepOverlay(mep, discipline) {
  if (!mep) return null;
  if (discipline === "electrical") {
    return {
      lines: asLine(mep.elec_wires, "#94A3B8", "4 3", 1.2),
      points: (mep.electrical || []).map((e) => ({ x: e.x, y: e.y, color: elecColor(e.type), r: 3.2 })),
    };
  }
  if (discipline === "plumbing") {
    return {
      lines: [...asLine(mep.cold_pipes, "#2563EB", null, 2), ...asLine(mep.hot_pipes, "#DC2626", null, 2)],
      points: [
        ...asPts((mep.plumbing || []).filter((p) => p.type !== "drain"), "#2563EB", 3.2),
        ...asPts((mep.service || []).filter((s) => s.type === "water_inlet"), "#1D4ED8", 5),
      ],
    };
  }
  // drainage
  return {
    lines: [...asLine(mep.drain_pipes, "#15803D", null, 2.4), ...asLine(mep.vent_pipes, "#0891B2", "3 2", 1.6)],
    points: [
      ...asPts((mep.plumbing || []).filter((p) => p.type === "drain"), "#15803D", 3.4),
      ...asPts((mep.service || []).filter((s) => s.type === "soil_stack"), "#166534", 6),
      ...asPts((mep.service || []).filter((s) => s.type === "sewer_connection"), "#0F766E", 5),
    ],
  };
}

/** Structural overlay: grid axes + isolated footings under each column. */
function structOverlay(struct, extW, extH) {
  if (!struct) return null;
  const grid = struct.grid || {};
  const lines = [
    ...(grid.x_labels || []).map((a) => ({ pts: [{ x: a.x, y: 0 }, { x: a.x, y: extH || 0 }], color: "#A78BFA", dash: "2 5", width: 0.7 })),
    ...(grid.y_labels || []).map((a) => ({ pts: [{ x: 0, y: a.y }, { x: extW || 0, y: a.y }], color: "#A78BFA", dash: "2 5", width: 0.7 })),
  ];
  const points = (struct.footings || []).map((f) => ({
    x: f.cx, y: f.cy, color: "#7C3AED", r: 6,
    label: `${Math.round((f.w || 0) * 100)}×${Math.round((f.h || 0) * 100)}`,
  }));
  return { lines, points };
}

/** Pool overlay (from open zones): outline + circulation equipment markers. */
function poolOverlay(zones) {
  const pools = (zones || []).filter((z) =>
    /pool|مسبح/i.test(`${z.label || ""} ${z.zone_kind || ""}`) && Number.isFinite(z.w) && Number.isFinite(z.h));
  const lines = [];
  const points = [];
  pools.forEach((p) => {
    const x0 = p.x - p.w / 2, x1 = p.x + p.w / 2, y0 = p.y - p.h / 2, y1 = p.y + p.h / 2;
    lines.push({ pts: [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }, { x: x0, y: y0 }], color: "#0EA5E9", width: 2 });
    points.push({ x: x0 + 0.3, y: y0 + 0.3, color: "#0284C7", r: 4, label: "SKIMMER" });
    points.push({ x: x1 - 0.3, y: y1 - 0.3, color: "#0EA5E9", r: 4, label: "RETURN" });
    points.push({ x: p.x, y: p.y, color: "#1E40AF", r: 4, label: "DRAIN" });
    points.push({ x: x1 + 0.6, y: p.y, color: "#7C3AED", r: 5, label: "PUMP" });
  });
  return { pools, overlay: { lines, points } };
}

export default function FloorPlans() {
  const { lang, user } = useAuth();
  const isAr = lang === "ar";
  const [wm] = useWorkingModel();
  const [active, setActive] = useState(0);
  const [discipline, setDiscipline] = useState("arch");
  const [mep, setMep] = useState(null);
  const [struct, setStruct] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showCirc, setShowCirc] = useState(false);
  const [circ, setCirc] = useState(null);
  const [showTB, setShowTB] = useState(true);

  const { data, loading, error } = useDeliverable(
    () => api.editorFloorPlans({ model: wm.model, floors_total: wm.floors || 1, wall_height_m: 3.2, include_roof: true }),
    [wm?.title],
    !!wm?.model
  );

  const variants = data?.variants || [];
  const cur = variants[active];
  const model = cur ? { walls: cur.walls, openings: cur.openings, columns: cur.columns, room_labels_pos: cur.zones, extent_w: wm?.model?.extent_w, extent_h: wm?.model?.extent_h, entry_markers: wm?.model?.entry_markers, facade_meta: wm?.model?.facade_meta } : null;

  const isMep = discipline === "electrical" || discipline === "plumbing" || discipline === "drainage";

  useEffect(() => {
    if (!model) return;
    let cancel = false;
    if (isMep && !mep) {
      setBusy(true);
      api.editorMEP({ model }).then((r) => { if (!cancel) setMep(r); }).catch(() => {}).finally(() => { if (!cancel) setBusy(false); });
    } else if (discipline === "structural" && !struct) {
      setBusy(true);
      api.editorSmartFoundation({ model, floors: wm.floors || 1, building_kind: "residential", slab_system: "solid", soil_bearing: 150 })
        .then((r) => { if (!cancel) setStruct(r); }).catch(() => {}).finally(() => { if (!cancel) setBusy(false); });
    }
    return () => { cancel = true; };
  }, [active, discipline, wm?.title]);

  // Reset cached discipline data when the floor changes.
  useEffect(() => { setMep(null); setStruct(null); setCirc(null); }, [active, wm?.title]);

  // Fetch the circulation map (entrance → corridors → rooms) on demand.
  useEffect(() => {
    if (!showCirc || !model || circ) return;
    let cancel = false;
    api.editorCirculation({ model }).then((r) => { if (!cancel) setCirc(r); }).catch(() => {});
    return () => { cancel = true; };
  }, [showCirc, model, circ]);

  const poolData = useMemo(() => poolOverlay(cur?.zones), [cur]);
  const overlay = useMemo(() => {
    if (isMep) return mepOverlay(mep, discipline);
    if (discipline === "structural") return structOverlay(struct, wm?.model?.extent_w, wm?.model?.extent_h);
    if (discipline === "pool") return poolData.overlay;
    return null;
  }, [discipline, mep, struct, poolData, isMep, wm]);

  const m = mep?.metrics || {};
  const s = struct?.summary || {};
  const showBlocks = discipline === "arch" || discipline === "structural";

  // Auto sheet number + title per discipline (professional numbering).
  const SHEET = {
    arch: { no: "A-01", ar: "مسقط معماري", en: "Architectural plan" },
    structural: { no: "S-01", ar: "مسقط إنشائي", en: "Structural plan" },
    electrical: { no: "E-01", ar: "مخطط كهرباء", en: "Electrical plan" },
    plumbing: { no: "P-01", ar: "مخطط تغذية مياه", en: "Water supply plan" },
    drainage: { no: "P-02", ar: "مخطط صرف صحي", en: "Drainage plan" },
    pool: { no: "M-01", ar: "مخطط المسابح", en: "Pool plan" },
  };
  const sheet = SHEET[discipline] || SHEET.arch;
  const levelLabel = cur ? (isAr ? cur.level_label_ar : cur.level_label_en) : "";
  const sheetTitle = `${isAr ? sheet.ar : sheet.en}${levelLabel ? ` · ${levelLabel}` : ""}`;

  return (
    <RequireModel>
      {() => (
        <div className="mx-auto max-w-6xl px-4 py-6">
          <PageHeader eyebrow={isAr ? "المساقط" : "Floor Plans"} title={isAr ? "المساقط لكل طابق وتخصص" : "Per-floor & per-discipline plans"} subtitle={isAr ? "معماري · إنشائي · كهرباء · مياه · صرف · مسابح - مع تحديد الواجهة وسماكة البلوك والحسابات" : "Arch · structural · electrical · water · drainage · pools - with facade, block size & calcs"} />
          <PlanActions
            model={model}
            projectName={wm.title}
            sheet={discipline === "structural" ? "structural" : (discipline === "electrical" ? "electrical" : (discipline === "plumbing" || discipline === "drainage") ? "plumbing" : (discipline === "pool" ? "pool" : "architectural"))}
            sheetMeta={{ floors: wm?.model?.floors || 1 }}
            testIdPrefix="floor-plans-actions"
          />
          <div className="mb-3"><EngineeringDetailsButton isAr={isAr} /></div>
          <DeliverableState loading={loading} error={error} empty={!variants.length} isAr={isAr}>
            <div className="mb-3 flex flex-wrap gap-2">
              {variants.map((v, i) => (
                <Button key={v.level_key} size="sm" variant={active === i ? "primary" : "outline"} onClick={() => setActive(i)} data-testid={`floor-${v.level_key}`}>
                  {isAr ? v.level_label_ar : v.level_label_en}
                </Button>
              ))}
            </div>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {DISCIPLINES.map((d) => (
                <Button key={d.key} size="sm" variant={discipline === d.key ? "accent" : "outline"} onClick={() => setDiscipline(d.key)} data-testid={`discipline-${d.key}`}>
                  {isAr ? d.ar : d.en}
                </Button>
              ))}
              <Button size="sm" variant={showCirc ? "primary" : "outline"} onClick={() => setShowCirc((v) => !v)} data-testid="toggle-circulation">
                {isAr ? "خريطة الحركة" : "Circulation map"}
              </Button>
              <Button size="sm" variant={showTB ? "primary" : "outline"} onClick={() => setShowTB((v) => !v)} data-testid="toggle-title-block">
                {isAr ? "بلوك العنوان" : "Title block"}
              </Button>
              {showCirc && circ && (
                <span data-testid="circulation-status" className={`rounded-full px-3 py-1 text-xs font-bold ${circ.ok ? "bg-emerald-500/15 text-emerald-600" : "bg-red-500/15 text-red-600"}`}>
                  {circ.ok ? (isAr ? "✓ كل الغرف موصولة بالممرات" : "✓ All rooms reach circulation") : (isAr ? `⚠ ${circ.violations.length} غرفة بمسار غير سليم` : `⚠ ${circ.violations.length} room(s) with bad access`)}
                </span>
              )}
            </div>

            {model && (
              <ZoomPan className="h-[560px]">
                <div className="min-w-[700px] p-4">
                  <PlanSVG model={model} w={900} h={640} showScale showLegend showDims overlay={overlay} circulation={showCirc ? circ : null} showBlocks={showBlocks} explain lang={lang} />
                </div>
              </ZoomPan>
            )}

            {model && showTB && (
              <TitleBlock isAr={isAr} projectName={wm?.title} sheetTitle={sheetTitle} sheetNo={sheet.no} scale="1:100" designCode={wm?.design_code} firm={user?.firm_profile} />
            )}

            {discipline !== "arch" && (
              <div className="mt-3 rounded-lg border border-border bg-card/50 p-3 text-xs" data-testid="discipline-calc-panel">
                {busy ? (
                  <span className="text-muted-foreground">{isAr ? "جارٍ الحساب..." : "Computing..."}</span>
                ) : discipline === "structural" ? (
                  <p className="text-muted-foreground">
                    <b className="text-foreground">{isAr ? "إنشائي" : "Structural"}:</b>{" "}
                    {isAr ? "أعمدة" : "columns"} <b className="text-foreground">{(struct?.columns || []).length}</b> ·{" "}
                    {isAr ? "خرسانة القواعد" : "footing concrete"} <b className="text-foreground">{s.total_concrete_m3_footings ?? 0} m³</b> ·{" "}
                    {isAr ? "خرسانة الأعمدة" : "column concrete"} <b className="text-foreground">{s.total_concrete_m3_columns ?? 0} m³</b>. {isAr ? "قواعد منفصلة مقاسة حسب الحمل ومحاور شبكية (A,B,C / 1,2,3). المقاس بجانب كل قاعدة بالسنتيمتر." : "Isolated footings sized by load, on a structural grid (A,B,C / 1,2,3). Size in cm next to each footing."}
                  </p>
                ) : discipline === "electrical" ? (
                  <p className="text-muted-foreground">
                    <b className="text-foreground">{isAr ? "كهرباء" : "Electrical"}:</b>{" "}
                    {isAr ? "الحمل المقدّر" : "Est. load"} <b className="text-foreground">{m.estimated_load_kw ?? 0} kW</b> ·{" "}
                    {isAr ? "نقاط" : "points"} <b className="text-foreground">{m.electrical_count ?? 0}</b> ·{" "}
                    {isAr ? "أسلاك" : "wire"} <b className="text-foreground">{Math.round(m.total_wire_m ?? 0)} m</b>. {isAr ? "الإنارة والمفاتيح والمقابس موزّعة لكل غرفة وموصولة باللوحة الرئيسية." : "Lighting, switches and sockets are distributed per room and wired to the main DB."}
                  </p>
                ) : discipline === "plumbing" ? (
                  <p className="text-muted-foreground">
                    <b className="text-foreground">{isAr ? "تغذية مياه (حار/بارد)" : "Water supply (hot/cold)"}:</b>{" "}
                    {isAr ? "بارد" : "cold"} <b className="text-foreground">{m.cold_water_m ?? 0} m</b> ·{" "}
                    {isAr ? "ساخن" : "hot"} <b className="text-foreground">{m.hot_water_m ?? 0} m</b>. {isAr ? "تبدأ من عداد المياه عند الواجهة وتغذّي كل نقطة رطبة (بارد أزرق · ساخن أحمر)." : "Starts at the front water meter and feeds every wet point (cold = blue · hot = red)."}
                  </p>
                ) : discipline === "drainage" ? (
                  <p className="text-muted-foreground">
                    <b className="text-foreground">{isAr ? "صرف صحي" : "Sanitary drainage"}:</b>{" "}
                    {isAr ? "طول الصرف" : "drainage"} <b className="text-foreground">{m.drainage_m ?? 0} m</b> ·{" "}
                    {isAr ? "الخط الرئيسي" : "main"} <b className="text-foreground">Ø{m.main_drain_dia_mm ?? 110} @ {m.drain_slope_pct ?? 1}%</b> ·{" "}
                    DFU <b className="text-foreground">{m.drain_fixture_units ?? 0}</b> ·{" "}
                    {isAr ? "تهوية" : "vent"} <b className="text-foreground">{m.vent_m ?? 0} m</b>. {isAr ? "المصارف تتجمّع في عمود رئيسي ثم خط مبنى بميل نحو المجاري (صرف أخضر · تهوية سماوي)." : "Drains collect into a soil stack then a sloped building drain (drain = green · vent = cyan)."}
                  </p>
                ) : (
                  <p className="text-muted-foreground">
                    <b className="text-foreground">{isAr ? "مسابح" : "Pools"}:</b>{" "}
                    {poolData.pools.length > 0 ? (() => {
                        const area = poolData.pools.reduce((a, p) => a + p.w * p.h, 0);
                        const vol = area * 1.5;
                        const flow = vol / 4;
                        return (
                          <>
                            {isAr ? "عدد" : "count"} <b className="text-foreground">{poolData.pools.length}</b> ·{" "}
                            {isAr ? "مساحة" : "area"} <b className="text-foreground">{area.toFixed(1)} m²</b> ·{" "}
                            {isAr ? "حجم (عمق 1.5م)" : "volume (1.5m)"} <b className="text-foreground">{vol.toFixed(1)} m³</b> ·{" "}
                            {isAr ? "التدفق المطلوب (دورة 4س)" : "flow (4h turnover)"} <b className="text-foreground">{flow.toFixed(1)} m³/h</b> ·{" "}
                            {isAr ? "مضخة" : "pump"} <b className="text-foreground">≥{Math.ceil(flow)} m³/h</b>. {isAr ? "دورة تصفية: سكيمر + مرجع + مصرف قاع + مضخة/فلتر رملي (موضّحة على المخطط)." : "Circulation: skimmer + return + main drain + sand pump/filter (shown on plan)."}
                          </>
                        );
                      })() : (isAr ? "لا يوجد مسبح في هذا المخطط. فعّل خيار المسبح في الاستوديو." : "No pool in this plan. Enable the pool option in Studio.")}
                  </p>
                )}
              </div>
            )}

            {cur && (isAr ? cur.notes_ar : cur.notes_en)?.length > 0 && discipline === "arch" && (
              <ul className="mt-3 list-disc space-y-1 ps-5 text-xs text-muted-foreground">
                {(isAr ? cur.notes_ar : cur.notes_en).map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            )}
          </DeliverableState>
        </div>
      )}
    </RequireModel>
  );
}
